import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Box, Typography, RadioGroup, FormControlLabel, Radio, Dialog } from "@mui/material";
import { useRef, useState, useEffect } from "react";
import { TextArea, FormField } from "./FormInputs";
import { useSelector, useDispatch } from "react-redux";
import CommonChip from "./CommonChip";
import { selectEntityType, selectUser } from "../redux/authSlice";
import { toast } from "./Toast/ToastUtils";
import { createApprovalActivity, getApprovalActivity, refreshMarketingCalendar, selectApprovalActivity } from "../redux/marketingCalendarSlice";
import { refetchClientAdApprovals, requestCloseTakeAction } from "../redux/clientAdApprovalSlice";
import { sendDraftEmailReady, sendDraftEmailActivity, sendPromotionCalendarReady, sendPromotionCalendarActivity } from "../services/emailService";
import { sendEmailNotification } from "../redux/emailSlice";
import CommonLoader from "./CommonLoader";
import AppButton from "./AppButton";
import { ApiCall } from "../helper/axios";
const TakeActionContent = ({ client_id, connection_id, campaign_id, currentStatus, marketingcalenderRefresh = false, clientAdApprovals = false, is_upload_show = false, data, hideUpdateForm = false, isQueue = false }) => {
    const dispatch = useDispatch();
    const fileRef = useRef(null);
    const [localHistory, setLocalHistory] = useState([]);
    const [fileLoading, setFileLoading] = useState(false);
    const [loadingHistory, setLoadingHistory] = useState(false);
    const user = useSelector(selectUser);
    const entityType = useSelector(selectEntityType);
    const approvalHistory = useSelector(selectApprovalActivity);
    const normalizeStatus = (s) => {
        const st = (s || "").toLowerCase();
        if (st === "approved" || st === "completed")
            return "approved";
        if (st === "rejected" || st === "not_approved" || st === "not approved")
            return "rejected";
        return "pending";
    };
    const [status, setStatus] = useState(() => normalizeStatus(currentStatus || data?.status));
    const [comment, setComment] = useState("");
    const [files, setFiles] = useState([]);
    const [visibleCount, setVisibleCount] = useState(3);
    const [previewImage, setPreviewImage] = useState(null);
    const [fileError, setFileError] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [fileProgress, setFileProgress] = useState({});
    const [fileUploading, setFileUploading] = useState({});
    const fileKey = (f) => `${f.name}_${f.size}_${f.lastModified}`;
    useEffect(() => {
        setStatus(normalizeStatus(currentStatus || data?.status));
    }, [currentStatus, data?.status, campaign_id]);
    const formatBytes = (bytes) => {
        if (!Number.isFinite(bytes) || bytes <= 0)
            return "0 B";
        const mb = bytes / (1024 * 1024);
        if (mb >= 1)
            return `${mb.toFixed(mb >= 10 ? 0 : 1)} MB`;
        const kb = bytes / 1024;
        return `${kb.toFixed(kb >= 10 ? 0 : 1)} KB`;
    };
    const formatDate = (d) => d.toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" });
    const getFileTypeLabel = (file) => {
        const ext = (file.name.split(".").pop() || "").toUpperCase();
        if (ext)
            return ext;
        const mime = (file.type || "").toLowerCase();
        if (mime.includes("jpeg"))
            return "JPG";
        if (mime.includes("png"))
            return "PNG";
        if (mime.includes("webp"))
            return "WEBP";
        if (mime.includes("pdf"))
            return "PDF";
        return "FILE";
    };
    const removeFile = (key) => {
        setFiles((prev) => prev.filter((f) => fileKey(f) !== key));
        setFileProgress((prev) => {
            const next = { ...prev };
            delete next[key];
            return next;
        });
        setFileUploading((prev) => {
            const next = { ...prev };
            delete next[key];
            return next;
        });
    };
    const openFilePicker = () => {
        fileRef.current?.click();
    };
    useEffect(() => {
        if (campaign_id) {
            setLocalHistory([]); // 🔥 clear old data
            setLoadingHistory(true);
            const moduleKey = marketingcalenderRefresh ? "marketing_calendar" : clientAdApprovals ? "ad_approvals" : "marketing_calendar";
            dispatch(getApprovalActivity({ campaign_id, client_id, module: moduleKey }))
                .unwrap()
                .then((res) => {
                setLocalHistory(res || []);
                const latest = (res || [])[0];
                const docStatus = normalizeStatus(currentStatus || data?.status);
                if (docStatus === "approved") {
                    setStatus("approved");
                }
                else if (latest?.status) {
                    setStatus(normalizeStatus(latest.status));
                }
            })
                .catch((e) => {
                console.error("Failed to fetch history:", e);
                setLocalHistory([]);
            })
                .finally(() => {
                setLoadingHistory(false);
            });
        }
    }, [campaign_id]);
    useEffect(() => {
        if (!files.length)
            return;
        const hasAnyUploading = submitting || Object.values(fileUploading).some((v) => v === true);
        if (!hasAnyUploading)
            return;
        const timer = window.setInterval(() => {
            setFileProgress((prev) => {
                const next = { ...prev };
                for (const f of files) {
                    const key = fileKey(f);
                    const cur = typeof next[key] === "number" ? next[key] : 0;
                    if (!submitting && fileUploading[key] !== true) {
                        continue;
                    }
                    const step = Math.max(3, Math.round((100 - cur) * 0.2));
                    next[key] = Math.min(cur + step, 95);
                }
                return next;
            });
            setFileUploading((prev) => {
                if (submitting)
                    return prev;
                let changed = false;
                const next = { ...prev };
                for (const f of files) {
                    const key = fileKey(f);
                    if (next[key] === true) {
                        const p = fileProgress[key] ?? 0;
                        if (p >= 95) {
                            next[key] = false;
                            changed = true;
                        }
                    }
                }
                return changed ? next : prev;
            });
        }, 300);
        return () => window.clearInterval(timer);
    }, [submitting, files, fileUploading, fileProgress]);
    // convert file → base64
    const convertBase64 = (file) => new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = error => reject(error);
    });
    const handleFileUpload = async (e) => {
        const uploadedFiles = Array.from(e.target.files || []);
        setFileError(null);
        const ALLOWED_MIME_TYPES = ["image/png", "image/jpeg", "image/jpg", "image/webp"];
        const ALLOWED_EXTENSIONS = [".png", ".jpg", ".jpeg", ".webp"];
        const validFiles = [];
        let hasInvalidFormat = false;
        let hasSizeExceeded = false;
        for (let i = 0; i < uploadedFiles.length; i++) {
            const file = uploadedFiles[i];
            const fileType = (file.type || "").toLowerCase();
            const fileName = (file.name || "").toLowerCase();
            const isValidFormat = ALLOWED_MIME_TYPES.includes(fileType) ||
                ALLOWED_EXTENSIONS.some((ext) => fileName.endsWith(ext));
            if (!isValidFormat) {
                hasInvalidFormat = true;
                continue;
            }
            if (file.size > 5 * 1024 * 1024) {
                hasSizeExceeded = true;
                continue;
            }
            validFiles.push(file);
        }
        if (hasInvalidFormat && hasSizeExceeded) {
            setFileError("Some files were skipped (only PNG, JPEG, WebP allowed, max 5MB).");
        }
        else if (hasInvalidFormat) {
            setFileError("Invalid file format. Supported formats: PNG, JPEG, WebP.");
        }
        else if (hasSizeExceeded) {
            setFileError("Some files were skipped (max 5MB each).");
        }
        if (validFiles.length === 0) {
            if (fileRef.current)
                fileRef.current.value = "";
            return;
        }
        const processedFiles = await Promise.all(validFiles.map(async (file) => ({
            name: file.name,
            type: file.type,
            size: file.size,
            lastModified: file.lastModified,
            data: await convertBase64(file)
        })));
        setFiles((prev) => {
            const existingKeys = new Set(prev.map((f) => fileKey(f)));
            const toAdd = processedFiles.filter((f) => !existingKeys.has(fileKey(f)));
            return [...prev, ...toAdd];
        });
        setFileProgress((prev) => {
            const next = { ...(prev || {}) };
            for (const f of processedFiles) {
                const key = fileKey(f);
                if (next[key] === undefined)
                    next[key] = 0;
            }
            return next;
        });
        setFileUploading((prev) => {
            const next = { ...(prev || {}) };
            for (const f of processedFiles) {
                next[fileKey(f)] = true;
            }
            return next;
        });
        if (fileRef.current)
            fileRef.current.value = "";
    };
    const handleSubmit = async () => {
        const moduleKey = marketingcalenderRefresh ? "marketing_calendar" : clientAdApprovals ? "ad_approvals" : "marketing_calendar";
        const payload = {
            send_by: entityType,
            user_id: user?.id,
            status: status === "rejected" ? "rejected" : status,
            comment,
            files,
            campaign_id,
            client_id,
            connection_id,
            module: moduleKey
        };
        setSubmitting(true);
        try {
            const result = await dispatch(createApprovalActivity(payload));
            if (createApprovalActivity.fulfilled.match(result)) {
                dispatch(getApprovalActivity({ campaign_id, client_id }));
                setComment("");
                setFiles([]);
                if (marketingcalenderRefresh) {
                    // ✅ 2. Dynamic Email Trigger based on selected status
                    const emailData = {
                        clientId: client_id,
                        actionBy: entityType === "client" ? "client" : "user",
                        storeName: data?.name || data?.storeName || "Store",
                        month: (data?.startDate || data?.send_time || "").split("-")?.[1] || "",
                        year: (data?.startDate || data?.send_time || "").split("-")?.[0] || "",
                        campaignId: campaign_id,
                        approvalStatus: status === "rejected" ? "not approved" : status,
                        comment,
                        showHistory: true
                    };
                    if (data?.type === "draft") {
                        // 🔒 Not yet visible to the client — the client hasn't been
                        // notified of this item at all, so status-based PROMOTION_*
                        // emails (which address the client directly) would be
                        // premature. Always send the same DRAFT_EMAIL_READY
                        // notification used by the "send for review" action instead,
                        // regardless of the selected status.
                        if (data?.visible_to_client === false) {
                            sendDraftEmailReady(emailData);
                        }
                        else {
                            // Visible to the client — any action (status change or
                            // just a comment) always fires this one activity
                            // notification, never the proposal-style PROMOTION_* keys.
                            sendDraftEmailActivity(emailData);
                        }
                    }
                    else if (data?.visible_to_client === false) {
                        // 🔒 Same rule as drafts — the client hasn't been sent this
                        // proposal yet, so status-based PROMOTION_* emails would be
                        // premature. Fire the same pair the dedicated "send" button
                        // fires, regardless of the selected status.
                        sendPromotionCalendarReady(emailData);
                    }
                    else {
                        // Visible to the client — any action (status change or
                        // just a comment) always fires this one activity
                        // notification, never the old per-status PROMOTION_* keys.
                        sendPromotionCalendarActivity(emailData);
                    }
                    dispatch(refreshMarketingCalendar());
                }
                if (clientAdApprovals) {
                    let mailType = "";
                    if (entityType === "client") {
                        mailType = isQueue ? "AD_REQUEST_RESULT" : "META_AD_ACTIVITY";
                    }
                    else {
                        if (!data?.sent_count) {
                            mailType = isQueue ? "AD_REQUEST_SEND" : "META_AD_REVIEW";
                        }
                        else {
                            mailType = isQueue ? "AD_REQUEST_UPDATE" : "META_AD_ACTIVITY";
                        }
                    }
                    await dispatch(sendEmailNotification({
                        type: mailType,
                        data: {
                            clientId: client_id,
                            storeId: client_id,
                            actionBy: entityType,
                            storeName: data?.storeName || data?.name || "Store",
                            campaignId: campaign_id,
                            month: (data?.startDate || data?.send_time || "").split("-")?.[1] || "",
                            year: (data?.startDate || data?.send_time || "").split("-")?.[0] || "",
                            approvalStatus: status === "rejected" ? "rejected" : status,
                            comment,
                            showHistory: true,
                            ads: [{ ad_name: data?.ad_name || (isQueue ? "Ad Request Update" : "Ad Approval Update"), status: status === "rejected" ? "rejected" : status, sent_count: data?.sent_count }],
                            adApprovalIds: [campaign_id]
                        }
                    })).unwrap();
                    dispatch(refetchClientAdApprovals());
                    dispatch(requestCloseTakeAction());
                }
            }
        }
        catch (e) {
            toast.error(e?.message || "Something went wrong");
        }
        finally {
            setSubmitting(false);
        }
    };
    const handleFileClick = async (logId, fileIndex, file) => {
        const fileUrl = typeof file === 'string' ? file : (file.url || file.data || file.path);
        if (fileUrl) {
            setPreviewImage(fileUrl);
            return;
        }
        // Fetch from API
        try {
            setFileLoading(true);
            const token = localStorage.getItem("V2_ReportingPortal") ? JSON.parse(localStorage.getItem("V2_ReportingPortal")).token : "";
            const response = await ApiCall("GET", `/api/approval-activity/${logId}/file/${fileIndex}`, null, { Authorization: token });
            if (response?.data?.success && response?.data?.data) {
                setPreviewImage(response.data.data);
            }
        }
        catch (error) {
            console.error("Error fetching file", error);
        }
        finally {
            setFileLoading(false);
        }
    };
    return (_jsxs(Box, { sx: { display: "flex", flexDirection: "column", gap: 1, position: "relative" }, children: [(submitting ? (_jsx(Box, { sx: {
                    height: "300px", // ya auto based
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center"
                }, children: _jsx(CommonLoader, { className: "table-loader" }) })) : (_jsxs(_Fragment, { children: [!hideUpdateForm && (_jsxs(Box, { sx: {
                            border: "1px solid #E5E7EB",
                            backgroundColor: "#ffff",
                            borderRadius: 1,
                            padding: 1,
                            display: "flex", flexDirection: "column", gap: 1
                        }, children: [_jsx("h3", { className: "all-Font-Sizes ", style: { color: "#FF008E", margin: 0, borderBottom: "1px solid #EAECF0", paddingBottom: 8 }, children: " Approval Status Update" }), _jsx(RadioGroup, { row: true, value: status, onChange: (e) => setStatus(e.target.value), children: ["pending", "approved", "rejected"].map((val) => (_jsx(FormControlLabel, { value: val, className: "all-Font-Sizes", label: val === "rejected"
                                        ? "Not Approved"
                                        : val.charAt(0).toUpperCase() + val.slice(1), control: _jsx(Radio, { sx: {
                                            color: "#FF008E",
                                            "&.Mui-checked": { color: "#FF008E" }
                                        } }) }, val))) }), _jsx(FormField, { children: _jsx(TextArea, { label: "Comment", minRows: 4, value: comment, onChange: (e) => setComment(e.target.value), placeholder: "Write comment..." }) }), is_upload_show &&
                                _jsxs(_Fragment, { children: [_jsx("h3", { className: "all-Font-Sizes ", style: { color: "#FF008E", margin: "8px 0", paddingBottom: 8, borderBottom: "1px solid #EAECF0", }, children: " Upload File" }), _jsxs(Box, { sx: {
                                                border: "1px dashed #D1D5DB",
                                                borderRadius: 1,
                                                padding: "28px",
                                                textAlign: "center",
                                                background: "#fff",
                                                cursor: "pointer"
                                            }, onClick: openFilePicker, onDragOver: (e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                            }, onDrop: (e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
                                                    handleFileUpload({ target: { files: e.dataTransfer.files } });
                                                }
                                            }, children: [_jsx("input", { ref: fileRef, type: "file", multiple: true, style: { display: "none" }, accept: "image/png,image/jpeg,image/webp,image/jpg,.png,.jpg,.jpeg,.webp", onChange: handleFileUpload }), _jsx(Box, { sx: {
                                                        width: 40,
                                                        height: 40,
                                                        borderRadius: "50%",
                                                        background: "#F3F4F6",
                                                        margin: "0 auto 10px",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center"
                                                    }, children: _jsx("img", { src: "/assets/upload.svg", alt: "" }) }), _jsxs(Typography, { children: ["Drag and Drop or", " ", _jsx("span", { style: { color: "#FF008E", fontWeight: 500 }, children: "Browse" }), " ", "to upload"] }), _jsx(Typography, { sx: { fontSize: 12, color: "#6B7280" }, children: "Supported Format: PNG, JPEG, WebP" })] }), fileError && (_jsxs(Typography, { sx: { color: "red", fontSize: 13, mt: 1, fontWeight: 500 }, children: ["* ", fileError] }))] }), Array.isArray(files) && files.length > 0 && (_jsx(Box, { sx: { display: "flex", flexDirection: "column", gap: 1.5, mt: 1 }, children: files.map((file) => {
                                    const key = fileKey(file);
                                    const progress = Math.max(0, Math.min(100, fileProgress[key] ?? 100));
                                    const isUploading = (submitting && progress < 100) || fileUploading[key] === true;
                                    return (_jsxs(Box, { sx: {
                                            borderRadius: "8px",
                                            border: isUploading ? "1px solid #F472B6" : "1px solid #E5E7EB",
                                            backgroundColor: isUploading ? "#FFF7FB" : "#FFFFFF",
                                            p: 1.5,
                                            display: "flex",
                                            flexDirection: "column",
                                            gap: 0.75,
                                        }, children: [_jsxs(Box, { sx: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }, children: [_jsxs(Box, { sx: { display: "flex", alignItems: "center", gap: 1.5 }, children: [_jsx(Box, { sx: {
                                                                    width: 32,
                                                                    height: 32,
                                                                    borderRadius: "8px",
                                                                    border: "1px solid #E5E7EB",
                                                                    display: "flex",
                                                                    alignItems: "center",
                                                                    justifyContent: "center",
                                                                    backgroundColor: "#FFFFFF",
                                                                    fontSize: 10,
                                                                    fontWeight: 600,
                                                                    color: "#6B7280",
                                                                }, children: getFileTypeLabel(file) }), _jsxs(Box, { sx: { display: "flex", flexDirection: "column" }, children: [_jsx(Typography, { sx: { fontSize: 13, fontWeight: 500, color: "#111827" }, children: file.name }), _jsxs(Typography, { sx: { fontSize: 11, color: "#6B7280" }, children: ["Date: ", formatDate(new Date(file.lastModified || Date.now())), "\u00A0 Size: ", formatBytes(file.size)] })] })] }), isUploading ? (_jsx(Box, { component: "button", type: "button", onClick: () => removeFile(key), disabled: submitting, sx: {
                                                            border: "none",
                                                            background: "transparent",
                                                            color: "#6B7280",
                                                            cursor: submitting ? "not-allowed" : "pointer",
                                                            fontSize: 18,
                                                            lineHeight: 1,
                                                            padding: 0,
                                                        }, "aria-label": "Remove file", children: "\u00D7" })) : (_jsxs(Box, { sx: { display: "flex", alignItems: "center", gap: 1.5, color: "#6B7280", fontSize: 12 }, children: [_jsx(Box, { component: "button", type: "button", onClick: () => removeFile(key), disabled: submitting, sx: {
                                                                    width: 18,
                                                                    height: 18,
                                                                    border: "none",
                                                                    display: "flex",
                                                                    alignItems: "center",
                                                                    justifyContent: "center",
                                                                    fontSize: 12,
                                                                    background: "#FFFFFF",
                                                                    cursor: submitting ? "not-allowed" : "pointer",
                                                                    padding: 0,
                                                                }, "aria-label": "Delete file", children: _jsx("img", { src: "/assets/delete.svg", alt: "" }) }), _jsx(Box, { component: "button", type: "button", onClick: () => {
                                                                    if (file.data)
                                                                        setPreviewImage(file.data);
                                                                }, sx: {
                                                                    width: 18,
                                                                    height: 18,
                                                                    border: "none",
                                                                    display: "flex",
                                                                    alignItems: "center",
                                                                    justifyContent: "center",
                                                                    fontSize: 12,
                                                                    background: "#FFFFFF",
                                                                    cursor: "pointer",
                                                                    padding: 0,
                                                                }, "aria-label": "Preview file", children: _jsx("img", { src: "/assets/eye.svg", alt: "" }) })] }))] }), isUploading && (_jsxs(Box, { sx: { ml: 5.5, mr: 1, mt: 0.5 }, children: [_jsxs(Box, { sx: { display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }, children: [_jsx(Typography, { sx: { fontSize: 11, fontWeight: 500, color: "#F472B6" }, children: "Uploading..." }), _jsxs(Typography, { sx: { fontSize: 11, fontWeight: 500, color: "#F472B6" }, children: [progress, "%"] })] }), _jsx(Box, { sx: { height: 4, width: "100%", backgroundColor: "#FCE7F3", borderRadius: 2, overflow: "hidden" }, children: _jsx(Box, { sx: {
                                                                height: "100%",
                                                                width: `${progress}%`,
                                                                backgroundColor: "#F472B6",
                                                                transition: "width 0.3s ease",
                                                            } }) })] })), !isUploading && (_jsx(Typography, { sx: { fontSize: 11, fontWeight: 500, color: "#16A34A", ml: 5.5 }, children: "Success" }))] }, key));
                                }) })), _jsx("div", { style: {
                                    display: "flex",
                                    justifyContent: "end"
                                }, children: _jsxs(AppButton, { variant: "contained", sx: {
                                        mt: 2,
                                        background: "#FF008E",
                                        borderColor: "#FF008E",
                                        "&:hover": { background: "#db2777" },
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1
                                    }, onClick: handleSubmit, loading: submitting, children: [_jsx("img", { src: "/assets/submit.svg", alt: "" }), "Save & Send"] }) })] })), _jsxs(Box, { sx: {
                            border: "1px solid #E5E7EB",
                            backgroundColor: "#FFFFFF",
                            borderRadius: "8px",
                            padding: "8px"
                        }, children: [_jsx(Typography, { className: "all-Font-Sizes ", sx: { color: "#FF008E", fontWeight: 600, pb: 1, borderBottom: "1px solid #EAECF0", mb: "32px" }, children: "History" }), !loadingHistory && (!localHistory || localHistory.length === 0) && (_jsx(Typography, { fontSize: "14px", color: "#6B7280", children: "No activity history yet. Actions and approvals will appear here." })), _jsxs(Box, { sx: {
                                    maxHeight: "500px",
                                    overflow: "hidden",
                                    overflowY: "auto",
                                    "&::-webkit-scrollbar": { display: "none" },
                                    msOverflowStyle: "none",
                                    scrollbarWidth: "none",
                                }, children: [(localHistory || []).slice(0, visibleCount).map((item, index) => {
                                        let initials = "BN";
                                        if (item.sender_first_name || item.sender_last_name) {
                                            initials = ((item.sender_first_name?.[0] || "") + (item.sender_last_name?.[0] || "")).toUpperCase();
                                        }
                                        const isLastVisible = index === Math.min(visibleCount, approvalHistory.length) - 1;
                                        const hasMore = approvalHistory.length > visibleCount;
                                        const timeFormatter = (dateStr) => {
                                            if (!dateStr)
                                                return "";
                                            const d = new Date(dateStr);
                                            const diffInSec = Math.floor((new Date().getTime() - d.getTime()) / 1000);
                                            if (diffInSec < 60)
                                                return "Just now";
                                            const diffInMin = Math.floor(diffInSec / 60);
                                            if (diffInMin < 60)
                                                return `${diffInMin} min ago`;
                                            const diffInHours = Math.floor(diffInMin / 60);
                                            if (diffInHours < 24)
                                                return `${diffInHours} hours ago`;
                                            return `${d.toLocaleString('en-US', { month: 'long', day: 'numeric' })} ${d.getFullYear()}, ${d.toLocaleString('en-US', { hour: 'numeric', minute: 'numeric', hour12: true }).toLowerCase()}`;
                                        };
                                        return (_jsxs(Box, { sx: { display: "flex", gap: "8px", position: "relative", mb: isLastVisible && !hasMore ? 0 : "8px" }, children: [(!isLastVisible || hasMore) && (_jsx(Box, { sx: {
                                                        position: "absolute",
                                                        top: "36px",
                                                        bottom: "-32px",
                                                        left: "17px",
                                                        width: "1px",
                                                        backgroundColor: "#E5E7EB",
                                                        zIndex: 0
                                                    } })), _jsx(Box, { sx: {
                                                        width: "36px",
                                                        height: "36px",
                                                        borderRadius: "8px",
                                                        border: "1px solid #E5E7EB",
                                                        background: "#FFFFFF",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        fontSize: "14px",
                                                        fontWeight: 600,
                                                        color: "#3B82F6",
                                                        zIndex: 1,
                                                        flexShrink: 0
                                                    }, children: initials }), _jsxs(Box, { sx: { width: "100%", mt: "-2px" }, children: [_jsxs(Box, { sx: { display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap", }, children: [_jsxs(Typography, { className: "all-Font-Sizes", sx: { fontWeight: 600, color: "#111827", display: "flex", gap: 1, alignItems: "center" }, children: [item.sender_first_name || "Client", " ", item.sender_last_name, _jsxs("span", { style: { fontWeight: 400, color: "#6B7280", fontSize: "14px" }, children: ["(", item.sender_designation || item.sender_role || "Client", ")"] })] }), item.status && (_jsx(CommonChip, { label: item.status === "rejected" ? "Not Approved" : item.status.charAt(0).toUpperCase() + item.status.slice(1), index: item.status === 'approved'
                                                                        ? 2
                                                                        : item.status === 'rejected'
                                                                            ? 0
                                                                            : item.status === 'pending'
                                                                                ? 1
                                                                                : 3, renderType: "tag", sx: { ml: "auto", fontWeight: 500 } }))] }), _jsx(Typography, { sx: { fontSize: "12px", color: "#6B7280", mt: "2px", mb: "8px" }, children: timeFormatter(item.createdAt || new Date().toISOString()) }), item.comment && (_jsx(Typography, { sx: { fontSize: "13px", color: "#5a6370", lineHeight: 1.5, mb: "8px" }, children: item.comment })), item.files?.length > 0 && (_jsx(Box, { sx: { display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }, children: item.files.map((file, fileIndex) => {
                                                                const fileUrl = typeof file === 'string' ? file : (file.url || file.data || file.path);
                                                                return (_jsxs(Box, { onClick: () => handleFileClick(item._id, fileIndex, file), title: file.name || "View File", sx: {
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        gap: 1,
                                                                        padding: '6px 10px',
                                                                        borderRadius: '6px',
                                                                        border: '1px solid #E5E7EB',
                                                                        backgroundColor: '#F9FAFB',
                                                                        cursor: 'pointer',
                                                                        '&:hover': { backgroundColor: '#F3F4F6', borderColor: '#D1D5DB' }
                                                                    }, children: [_jsxs("svg", { width: "16", height: "16", viewBox: "0 0 24 24", fill: "none", stroke: "#FF008E", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", children: [_jsx("rect", { x: "3", y: "3", width: "18", height: "18", rx: "2", ry: "2" }), _jsx("circle", { cx: "8.5", cy: "8.5", r: "1.5" }), _jsx("polyline", { points: "21 15 16 10 5 21" })] }), _jsx(Typography, { className: "all-Font-Sizes", color: "#374151", fontWeight: 500, children: file.name ? (file.name.length > 20 ? file.name.substring(0, 20) + '...' : file.name) : `Attachment ${fileIndex + 1}` })] }, fileIndex));
                                                            }) }))] })] }, item._id || index));
                                    }), approvalHistory && approvalHistory.length > visibleCount && (_jsxs(Box, { sx: { display: "flex", position: "relative", mt: '22px' }, children: [_jsx(Box, { sx: {
                                                    position: "absolute",
                                                    top: "-32px",
                                                    left: "17px",
                                                    width: "22px",
                                                    height: "48px",
                                                    borderBottom: "1px solid #E5E7EB",
                                                    borderLeft: "1px solid #E5E7EB",
                                                    borderBottomLeftRadius: "8px",
                                                    zIndex: 0
                                                } }), _jsxs(AppButton, { variant: "outlined", onClick: () => setVisibleCount((prev) => prev + 4), sx: {
                                                    ml: "39px",
                                                    textTransform: "none",
                                                    borderColor: "#E5E7EB",
                                                    color: "#111827",
                                                    fontWeight: 600,
                                                    borderRadius: "8px",
                                                    py: "4px",
                                                    px: "12px",
                                                    fontSize: "14px",
                                                    backgroundColor: "#FFFFFF",
                                                    "&:hover": { borderColor: "#D1D5DB", backgroundColor: "#F9FAFB" },
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: 1
                                                }, children: [_jsx("svg", { width: "16", height: "16", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", children: _jsx("path", { d: "M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.3" }) }), "View More (", approvalHistory.length - visibleCount, ")"] })] }))] })] })] }))), _jsxs(Dialog, { open: !!previewImage, onClose: () => setPreviewImage(null), maxWidth: "lg", fullWidth: true, children: [_jsxs(Box, { sx: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, borderBottom: '1px solid #E5E7EB' }, children: [_jsxs(Typography, { sx: { fontWeight: 600, fontSize: '16px', color: '#111827' }, children: ["File Preview ", fileLoading && "(Loading...)"] }), _jsx(Box, { sx: {
                                    cursor: 'pointer',
                                    color: '#6B7280',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    '&:hover': { color: '#111827' },
                                }, onClick: () => setPreviewImage(null), children: _jsxs("svg", { width: "24", height: "24", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", children: [_jsx("line", { x1: "18", y1: "6", x2: "6", y2: "18" }), _jsx("line", { x1: "6", y1: "6", x2: "18", y2: "18" })] }) })] }), _jsx(Box, { sx: { p: 2, display: 'flex', justifyContent: 'center', background: '#F9FAFB' }, children: previewImage && (previewImage.includes('.pdf') || previewImage.includes('application/pdf') ? (_jsx("iframe", { src: `${previewImage}#toolbar=0&navpanes=0`, style: { width: '100%', height: '80vh', border: '1px solid #E5E7EB', background: 'white', borderRadius: '8px' } })) : (_jsx("img", { src: previewImage, alt: "Preview", style: { maxWidth: '100%', maxHeight: '80vh', objectFit: 'contain', borderRadius: '8px', border: '1px solid #E5E7EB', background: 'white' } }))) })] })] }));
};
export default TakeActionContent;
